import React, { useState, useEffect, useRef, useCallback } from "react";
import Logo from "../assets/img/logo.svg";
import { searchInJson } from "../helpers/Helper";
import SidebarItems from "./SidebarItems";
import { MdClear, MdChevronLeft, MdChevronRight, MdDeleteForever } from "react-icons/md";
import { FiRefreshCw, FiPlay, FiPause, FiEye, FiEyeOff } from "react-icons/fi";
import { truncateAllItems } from "../api/integrations";

const SidebarControls = ({ fetchData }) => {
  const [isPaused, setIsPaused] = useState(true)
  const [isHardPulse, setIsHardPulse] = useState(false)
  const tickRef = useRef(0)
  const intervalRef = useRef(null)

  const handleRefresh = useCallback(async () => {
    try {
      if (fetchData) {
        await fetchData()
      }
    } catch (e) {
      console.error('Refresh failed', e)
    }
  }, [fetchData])
  const handleRefreshWithPulse = async () => {
    if (!isPaused) {
      setIsHardPulse(true)
      setTimeout(() => setIsHardPulse(false), 300)
    }
    await handleRefresh()
  }
  const togglePause = () => setIsPaused(prev => !prev)

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    tickRef.current = 0
    setIsHardPulse(false)

    if (!isPaused) {
      intervalRef.current = setInterval(async () => {
        const next = (tickRef.current + 1) % 2
        tickRef.current = next
        if (next === 0) {
          setIsHardPulse(true)
          try {
            await handleRefresh()
          } finally {
            setTimeout(() => setIsHardPulse(false), 300)
          }
        }
      }, 1000)
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isPaused, handleRefresh])

  // Abstract UI properties for clarity
  const getPauseButtonClass = () => {
    if (isPaused) {
      return undefined
    }
    if (isHardPulse) {
      return 'is-running hard'
    }
    return 'is-running'
  }
  const getPauseIconClass = () => {
    if (isHardPulse) {
      return 'pulse-icon hard'
    }
    return 'pulse-icon'
  }
  const pauseButtonClass = getPauseButtonClass()
  const pauseIconClass = getPauseIconClass()
  const pauseIconElement = isPaused ? (
    <FiPlay size={20} />
  ) : (
    <FiPause size={20} className={pauseIconClass} />
  )

  return (
    <>
      <button onClick={handleRefreshWithPulse} title="Refresh">
        <FiRefreshCw size={20} />
      </button>
      <button
        onClick={togglePause}
        className={pauseButtonClass}
        title={isPaused ? 'Play' : 'Pause'}
      >
        {pauseIconElement}
      </button>
    </>
  )
}

const Sidebar = (props) => {
  const [searchHits, setSearchHits] = useState([]);
  const { data, setSearchTerm, searchTerm, setSelectedIndex, selectedIndex, fetchData, showUnfiltered, setShowUnfiltered } =
    props;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // Keyboard navigation: ArrowUp/ArrowDown cycles selectedIndex
  useEffect(() => {
    const handler = (e) => {
      if (!data || !data.length) {
        return;
      }
      if (e.altKey || e.ctrlKey || e.metaKey) {
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        let index = selectedIndex + 1;
        if (index > data.length - 1) {
          index = 0;
        }
        setSelectedIndex(index);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        let index = selectedIndex - 1;
        if (index < 0) {
          index = data.length - 1;
        }
        setSelectedIndex(index);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [data, selectedIndex, setSelectedIndex]);

  const getSearchHits = (list, term) => {
    const hits = []
    if (!list || !list.length) {
      return hits
    }
    for (let i = 0; i < list.length; i++) {
      const item = list[i]
      let body = item && item.body
      if (typeof body === 'string') {
        try {
          body = JSON.parse(body)
        } catch (_) {
          body = null
        }
      }
      const src = body && (body.entireEvent || body)
      if (searchInJson(src, term)) {
        hits.push(i)
      }
    }
    return hits
  }

  const handleSearchChange = (event) => {
    const value = event.target.value
    const results = getSearchHits(data, value)
    setSearchHits(results)
    setSearchTerm(value)
  };

  // Keep search results in sync when data is fetched/updated or when the term changes
  useEffect(() => {
    const results = getSearchHits(data, searchTerm)
    setSearchHits(results)
  }, [data, searchTerm])

  // Smoothly scroll the sidebar results to top when new data is fetched/updated
  const contentRef = useRef(null)
  useEffect(() => {
    if (contentRef.current) {
      try {
        contentRef.current.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (_) {
        // Fallback for older browsers/environments that don't support smooth behavior
        contentRef.current.scrollTop = 0
      }
    }
  }, [data])

  const goToNextResult = () => {
    let index = selectedIndex + 1;
    if (index > data.length - 1) {
      index = 0;
    }

    setSelectedIndex(index);
  };

  const goToPreviousResult = () => {
    let index = selectedIndex - 1;
    if (index < 0) {
      index = data.length - 1;
    }

    setSelectedIndex(index);
  };

  const openDeleteModal = () => {
    setDeleteError("");
    setShowDeleteModal(true);
  };
  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
  };
  const confirmDeleteAll = async () => {
    try {
      setDeleteError("");
      setIsDeleting(true);
      await truncateAllItems();
      setShowDeleteModal(false);
      if (fetchData) {
        try { await fetchData(); } catch (_) { /* noop */ }
      }
    } catch (e) {
      setDeleteError("Failed to delete all items. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const isInternalExtension = (number) => {
    const modifiedNumber = number.replace(/[#*/]/g, "");
    const num = parseInt(modifiedNumber, 10);
    if (isNaN(num)) {
      console.error("Invalid number input:", number);
      return false;
    }
    return num >= 0 && num <= 999;
  };

  const getContextInfo = (body) => {
    // With new payload shape, all event props live under body.entireEvent.
    // Keep backward-compatible fallback to body if entireEvent is missing.
    const src = body && (body.entireEvent || body);
    if (src) {
      const caller = src["Caller-Caller-ID-Number"];
      const destination = src["Caller-Destination-Number"];
      const callState = src["Channel-Call-State"];
      const eventName = src["Event-Name"];
      const userExtension = src["Channel-Extension"];

      let callDirection = "Unknown call direction";

      if (caller && destination && callState) {
        const isCallerInternal = isInternalExtension(String(caller));
        const isDestinationInternal = isInternalExtension(String(destination));

        if (isCallerInternal && isDestinationInternal) {
          callDirection = `Internal: ${caller} to ${destination} - For ${userExtension}`;
        } else if (isCallerInternal) {
          callDirection = `Outgoing: ${caller} to ${destination} - For ${userExtension}`;
        } else if (isDestinationInternal) {
          callDirection = `Incoming: ${destination} by ${caller} - For ${userExtension}`;
        }

        return `${callState} | ${eventName} - Dir: ${callDirection}`;
      }
    }
    return "No call information.";
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <img alt="TelSmart Logo" title="TelSmart Logo" src={Logo} className="logo" />
        {/* First line: search input and delete button */}
        <div className="search-row" role="group">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder='Search by "FULL FS EVENT" key and/or value. Use comma to separate multiple search terms...'
            className="search-input"
            disabled={!data}
            aria-label="Search"
          />
          <button onClick={() => { setSearchTerm(""); setSearchHits([]); }} className="clear-button" title="Clear">
            <MdClear size={20} focusable="false" />
          </button>
        </div>
        <div className="search-hint" role="note">
          If a single search term is provided and it matches a key in the event, the corresponding value will be displayed in the sidebar.
        </div>
        {/* Second line: navigation (prev/next) and refresh and start */}
        <div className="controls" style={{marginTop: 8}}>
          <div className="controls-right">
            <button
              onClick={() => setShowUnfiltered && setShowUnfiltered((v) => !v)}
              title={showUnfiltered ? 'Hide non-filtered events' : 'Show non-filtered events'}
              className={showUnfiltered ? 'toggle-on' : 'toggle-off'}
            >
              {showUnfiltered ? <FiEye size={18} /> : <FiEyeOff size={18} />}
            </button>
            <SidebarControls fetchData={fetchData} />
          </div>
          <div className="controls-left">
            <button onClick={goToPreviousResult} disabled={!data} className="nav-button" title="Previous">
              <MdChevronLeft size={20} />
            </button>
            <button onClick={goToNextResult} disabled={!data} className="nav-button" title="Next">
              <MdChevronRight size={20} />
            </button>
            <button onClick={openDeleteModal} disabled={!data} className="nav-button danger-button" title="Delete all items">
              <MdDeleteForever size={20} />
            </button>
          </div>
        </div>
        {showDeleteModal && (
          <div className="modal-wrapper" role="dialog" aria-modal="true" aria-labelledby="confirm-delete-title">
            <div className="confirm-modal">
              <h3 id="confirm-delete-title">Delete all items?</h3>
              <p>This will permanently delete all items. This action cannot be undone.</p>
              {deleteError && <div className="error-text" role="alert">{deleteError}</div>}
              <div className="confirm-actions">
                <button onClick={closeDeleteModal} disabled={isDeleting}>Cancel</button>
                <button onClick={confirmDeleteAll} className="btn-danger" disabled={isDeleting}>
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
        <hr />
      </div>
      <div className="sidebar-content" ref={contentRef}>
        <SidebarItems
          data={data}
          selectedIndex={selectedIndex}
          searchHits={searchHits}
          getContextInfo={getContextInfo}
          setSelectedIndex={setSelectedIndex}
          searchTerm={searchTerm}
        />
      </div>
    </div>
  );
};

export default Sidebar;
